import React, { useState, useEffect } from "react";
import Spinner from "../../components/Spinner";
import AxiosInstance from "../../services/networkServices";
import PageLayout from "../../components/PageLayout";
import moment from "moment";
import {
  Avatar,
  Box,
  CssBaseline,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import { Visibility, PlayArrow, Person, KeyboardArrowDown } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import routePaths from "../../router/route-paths";
import CustomSwitch from "../../components/Switch";
import CommonPagination from "../../components/Table/CommonPagination";
import VideoPlayerDialog from "../../components/VideoPlayerDialog";
import ExpandableText from "../../components/ExpandableText";
import { limitChar, isVideoUrl, getPostThumbnail } from "../../utils/common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDownWideShort,
  faArrowDownShortWide,
} from "@fortawesome/free-solid-svg-icons";
import StatusFilter from "../../components/StatusFilter";
import typeOutline from "../../assets/typeOutline.svg";
import { showToast } from "../../store/slices/common";
import AccountActionDialog, {
  getAuthorAccountState,
} from "./AccountActionDialog";
const columnsPost = [
  { id: "postImage", label: "Post Image" },
  { id: "caption", label: "Caption" },
  { id: "latestReport", label: "Latest Report" },
  { id: "username", label: "Post Username" },
  { id: "createdAt", label: "Date of Creation" },
  { id: "contentStatus", label: "Post Status" },
  { id: "aiAnalysis", label: "AI Analysis" },
  { id: "status", label: "Report Status" },
  { id: "totalReportCount", label: "Count of Reports" },
  { id: "actions", label: "Actions" },
];

const columnsComment = [
  { id: "content", label: "Comment Content" },
  { id: "latestReport", label: "Latest Report" },
  { id: "username", label: "Username of Commenter" },
  { id: "createdAt", label: "Date of Creation" },
  { id: "contentStatus", label: "Comment Status" },
  { id: "aiAnalysis", label: "AI Analysis" },
  { id: "status", label: "Report Status" },
  { id: "totalReportCount", label: "Count of Reports" },
  { id: "actions", label: "Actions" },
];

// Automated moderation verdict attached by POST /report/list (`moderation`).
const AI_STATUS_CHIP = {
  PENDING: { label: "Queued", color: "default" },
  ANALYZING: { label: "Analyzing…", color: "info" },
  NO_VIOLATION: { label: "No violation", color: "success" },
  VIOLATION: { label: "Removed by AI", color: "error" },
  NOT_ANALYZED: { label: "Not analyzed", color: "default" },
  FAILED: { label: "Analysis failed", color: "warning" },
};

const renderAiAnalysisCell = (moderation) => {
  if (!moderation) {
    return (
      <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>—</Typography>
    );
  }
  const chip = AI_STATUS_CHIP[moderation.status] || AI_STATUS_CHIP.NOT_ANALYZED;
  const details = [
    moderation.provider ? `Provider: ${moderation.provider}` : null,
    moderation.matchedCategories?.length
      ? `Matched: ${moderation.matchedCategories.join(", ")}`
      : null,
    moderation.summary || null,
    moderation.banUntil
      ? `Author banned until ${moment(moderation.banUntil).format("MMM D, YYYY")}`
      : null,
    moderation.appealStatus && moderation.appealStatus !== "NONE"
      ? `Appeal: ${moderation.appealStatus}`
      : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-line" }}>{details}</span>}>
      <Box sx={{ display: "inline-flex", flexDirection: "column", gap: "4px" }}>
        <Chip
          size="small"
          label={chip.label}
          color={chip.color}
          sx={{ height: "20px", fontSize: "11px", fontWeight: 600 }}
        />
        {moderation.appealStatus && moderation.appealStatus !== "NONE" && (
          <Chip
            size="small"
            variant="outlined"
            label={`Appeal ${moderation.appealStatus.toLowerCase()}`}
            sx={{ height: "18px", fontSize: "10px" }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

// Cheating tickets come from the pagent result card: a participant reports
// the opponent's entry. Row `_id` is the accused entry (post) id.
const columnsCheating = [
  { id: "postImage", label: "Accused Entry" },
  { id: "pagent", label: "Pagent" },
  { id: "username", label: "Accused User" },
  { id: "reportedBy", label: "Reported By" },
  { id: "latestReport", label: "Report Text" },
  { id: "createdAt", label: "Latest Report Date" },
  { id: "contentStatus", label: "Entry Status" },
  { id: "status", label: "Report Status" },
  { id: "totalReportCount", label: "Count of Reports" },
  { id: "actions", label: "Actions" },
];

const MATCH_TYPE_LABELS = {
  MEDIA_POST: "Simple",
  TEXT_POST: "Text",
  PROMPT_POST: "Prompt",
  CONTEST_POST: "Contest",
  CATEGORY_POST: "Category",
  KARAOKE_POST: "Karaoke",
  DANCE_POST: "Dance",
  ART_POST: "Art",
  GROUP_DUET_POST: "Group Duet",
  RAP_BATTLE_POST: "Rap Battle",
};

const columnsUser = [
  { id: "profilePicture", label: "Profile" },
  { id: "username", label: "Username" },
  { id: "email", label: "Email" },
  { id: "latestReport", label: "Latest Report" },
  // For users the row-level createdAt is the newest report's date (there is
  // no "content creation" date like posts/comments have).
  { id: "createdAt", label: "Latest Report Date" },
  { id: "contentStatus", label: "Account Status" },
  { id: "status", label: "Report Status" },
  { id: "totalReportCount", label: "Count of Reports" },
  { id: "actions", label: "Actions" },
];
const postStyle = {
  position: "absolute",
  top: "-7px",
  right: "-10px",
  color: "rgb(20, 44, 92)",
  fontSize: "16px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1,
  border: "1px solid black",
  padding: "5px",
  backgroundColor: "#fbc46a",
  height: "25px",
  width: "25px",
  borderRadius: "50%",
};

// Every post type in the backend enum — previously anything that wasn't one
// of the first four fell through as "T" (Text) with a broken image.
const POST_TYPE_BADGES = {
  MEDIA_POST: { letter: "S", label: "Simple Post" },
  TEXT_POST: { letter: "T", label: "Text Post" },
  PROMPT_POST: { letter: "P", label: "Prompt Post" },
  CONTEST_POST: { letter: "C", label: "Contest Post" },
  CATEGORY_POST: { letter: "Cg", label: "Category Post" },
  KARAOKE_POST: { letter: "K", label: "Karaoke Post" },
  DANCE_POST: { letter: "D", label: "Dance Post" },
  ART_POST: { letter: "A", label: "Art Post" },
  GROUP_DUET_POST: { letter: "G", label: "Group Duet Post" },
  RAP_BATTLE_POST: { letter: "R", label: "Rap Battle Post" },
};
const REPORT_TABS = ["POST", "COMMENT", "USER", "LIVE", "CHEATING"];

const ManageTicket = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  // Detail pages navigate back with the tab they belong to so the admin
  // lands on the same list they came from.
  const [filterType, setFilterType] = useState(
    REPORT_TABS.includes(location.state?.filterType)
      ? location.state.filterType
      : "POST"
  );
  const [tickets, setTickets] = useState([]);
  const [page, setPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState("");
  const [postFilter, setPostFilter] = useState(0);
  const [videoDialog, setVideoDialog] = useState({
    open: false,
    url: "",
    fallbackUrl: "",
    title: "",
    isDisabled: false,
  });
  // Row id of the post/comment currently being disabled, so the button can't
  // be double-clicked while the request is in flight.
  const [disablingId, setDisablingId] = useState(null);
  const [accountMenu, setAccountMenu] = useState({
    anchorEl: null,
    ticket: null,
  });
  const [accountDialog, setAccountDialog] = useState({
    open: false,
    action: null,
    ticket: null,
  });
  const [accountActionBusy, setAccountActionBusy] = useState(false);
  const rowsPerPage = 15;

  useEffect(() => {
    fetchTickets();
  }, [filterType, page, rowsPerPage]);

  useEffect(() => {
    fetchTickets(1);
    setPage(0);
  }, [order, postFilter]);

  const sortOrderHandler = (sortOrder) => {
    setOrder(sortOrder);
  };

  const fetchTickets = async (pg) => {
    const pgPayload = pg ? pg : page + 1;
    setLoading(true);
    const payloadWithoutOrder = {
      page: pgPayload,
      limit: rowsPerPage,
      type: filterType,
    };
    const payloadWithOrder = { ...payloadWithoutOrder, orderBy: order };
    let payload = order ? payloadWithOrder : payloadWithoutOrder;
    if (postFilter) {
      payload = {
        ...payload,
        isResolved: postFilter === "Active" ? false : true,
      };
    }
    try {
      const response = await AxiosInstance.post("/report/list", payload);
      const data = response.data.data;
      const updatedTickets = data.list.map((ticket) => ({
        ...ticket,
        status: ticket.status || "Active",
      }));

      setTickets(updatedTickets);
      setTotalRows(data.resultCount);
    } catch (error) {
      console.error("Failed to fetch tickets", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBreadcrumbClick = (event, type) => {
    event.preventDefault();
    setFilterType(type);
    setPage(0);
  };

  const handleViewTicket = (ticketId) => {
    if (filterType === "POST") {
      navigate(`${routePaths.MANAGE_TICKET}/post/${ticketId}`);
    } else if (filterType === "COMMENT") {
      navigate(`${routePaths.MANAGE_TICKET}/comment/${ticketId}`);
    } else if (filterType === "USER" || filterType === "LIVE") {
      navigate(`${routePaths.MANAGE_TICKET}/user/${ticketId}`, {
        state: { filterType },
      });
    } else if (filterType === "CHEATING") {
      navigate(`${routePaths.MANAGE_TICKET}/cheating/${ticketId}`);
    }
  };

  const handleStatusToggle = async (ticketId, currentStatus) => {
    try {
      await AxiosInstance.post("/report/action", {
        action: currentStatus ? "DISABLE" : "RESOLVE",
        type: filterType,
        id: ticketId,
      });
      fetchTickets();
    } catch (error) {
      console.error("Failed to update status", error);
    }
  };

  // One-click disable of the reported post/comment/user itself (row `_id` is
  // the post/comment/user id — the report list groups reports by it). For
  // users this blocks the account. The backend also marks all reports for
  // that target as resolved.
  const handleDisableContent = async (ticket) => {
    setDisablingId(ticket._id);
    try {
      await AxiosInstance.post("/report/action", {
        action: "DISABLE",
        type: filterType,
        id: ticket._id,
      });
      await fetchTickets();
    } catch (error) {
      console.error("Failed to disable content", error);
    } finally {
      setDisablingId(null);
    }
  };

  const closeAccountMenu = () => {
    setAccountMenu({ anchorEl: null, ticket: null });
  };

  const openAccountDialog = (action, ticket) => {
    closeAccountMenu();
    setAccountDialog({ open: true, action, ticket });
  };

  const handleAccountAction = async () => {
    const ticket = accountDialog.ticket;
    const action = accountDialog.action;
    if (!ticket || !action) return;
    setAccountActionBusy(true);
    try {
      await AxiosInstance.post("/report/action", {
        action,
        type: filterType,
        id: ticket._id,
      });
      const username = getTicketAuthor(ticket)?.username;
      dispatch(
        showToast({
          color: "success",
          msg:
            action === "BAN_ACCOUNT"
              ? `@${username || "User"} has been banned`
              : `@${username || "User"}'s account has been disabled`,
        })
      );
      setAccountDialog({ open: false, action: null, ticket: null });
      await fetchTickets();
    } catch (error) {
      dispatch(
        showToast({
          color: "error",
          msg:
            error?.response?.data?.message ||
            "Failed to update the author's account",
        })
      );
    } finally {
      setAccountActionBusy(false);
    }
  };

  const formatDateString = (dateString) => {
    return moment(dateString).format("DD/MM/YY");
  };

  const columns =
    filterType === "COMMENT"
      ? columnsComment
      : filterType === "USER" || filterType === "LIVE"
      ? columnsUser
      : filterType === "CHEATING"
      ? columnsCheating
      : columnsPost;
  const isPostPage = filterType === "POST";
  const isCommentPage = filterType === "COMMENT";
  const isUserPage = filterType === "USER";
  const isLivePage = filterType === "LIVE";
  // Live-stream reports target a player account, so they share the user table.
  const isAccountPage = isUserPage || isLivePage;
  const isCheatingPage = filterType === "CHEATING";
  // Cheating tickets target a post (the accused entry), so they get every
  // post-level affordance: thumbnail, author state, account actions.
  const isPostLike = isPostPage || isCheatingPage;

  const getHeaderContent = (column) => {
    if (column?.id === "totalReportCount") {
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            sx={{
              maxWidth: {
                xs: filterType === "POST" ? "76px" : "90px",
                "@media (min-width: 1700px)": {
                  maxWidth: "none",
                },
              },
              fontSize: "16px",
            }}
          >
            {column.label}
          </Typography>
          {order === "ASC" ? (
            <FontAwesomeIcon
              icon={faArrowDownWideShort}
              onClick={() => sortOrderHandler("DESC")}
              style={{ cursor: "pointer", marginLeft: "8px", fontSize: "20px" }}
            />
          ) : (
            <FontAwesomeIcon
              icon={faArrowDownShortWide}
              onClick={() => sortOrderHandler("ASC")}
              style={{ cursor: "pointer", marginLeft: "8px", fontSize: "20px" }}
            />
          )}
        </Box>
      );
    } else if (column?.id === "status") {
      return (
        <Box sx={{ display: "flex" }}>
          <>
            {column.label} &nbsp;
            <StatusFilter isTicket={true} setPostFilter={setPostFilter} />
          </>
        </Box>
      );
    } else return column.label;
  };
  const getPostBadge = (ticket) =>
    POST_TYPE_BADGES[ticket?.postDetails?.type] || {
      letter: "?",
      label: ticket?.postDetails?.type || "Unknown",
    };
  // Posts keep the playable video in `media` and a poster frame in
  // `thumbnail` — never feed `media` to an <img>, it's usually .m3u8/.mp4.
  const getImage = (ticket) => getPostThumbnail(ticket?.postDetails) || typeOutline;

  const openVideoDialog = (ticket) => {
    const post = ticket?.postDetails;
    if (!post || !isVideoUrl(post.media)) return;
    setVideoDialog({
      open: true,
      url: post.media,
      fallbackUrl: post.originalMedia || "",
      title: post.caption || ticket?.postedBy?.username || "Reported Post",
      isDisabled: Boolean(post.isDisabled),
    });
  };

  // Clear, dedicated indicator for whether the reported content itself is
  // visible to users — independent of the report's Active/Resolved state.
  const getContentStatusChip = (details) => {
    if (!details) return "—";
    if (details.isDeleted)
      return (
        <Chip
          label="Deleted"
          size="small"
          sx={{ backgroundColor: "#9e9e9e", color: "#fff", fontWeight: 600 }}
        />
      );
    if (details.isDisabled)
      return (
        <Chip
          label="Disabled"
          size="small"
          color="error"
          sx={{ fontWeight: 600 }}
        />
      );
    return (
      <Chip
        label="Enabled"
        size="small"
        color="success"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  // Account counterpart of getContentStatusChip. A missing userDetails means
  // the account document is gone entirely — treat it as deleted.
  const getUserStatusChip = (user) => {
    if (!user || user.isDeleted)
      return (
        <Chip
          label="Deleted"
          size="small"
          sx={{ backgroundColor: "#9e9e9e", color: "#fff", fontWeight: 600 }}
        />
      );
    if (user.isAccountBlocked)
      return (
        <Chip
          label="Blocked"
          size="small"
          color="error"
          sx={{ fontWeight: 600 }}
        />
      );
    return (
      <Chip
        label="Active"
        size="small"
        color="success"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const getLatestReportCell = (ticket) => {
    const issue = ticket?.issue;
    const description = ticket?.issueDescription;
    if (!issue && !description) return "—";
    return (
      <Box sx={{ maxWidth: "220px" }}>
        {issue && (
          <Typography
            sx={{
              fontSize: "inherit",
              fontWeight: 600,
            }}
          >
            <ExpandableText text={issue} maxLength={25} title="Report reason" />
          </Typography>
        )}
        {description && (
          <Typography sx={{ fontSize: "13px", color: "#666" }}>
            <ExpandableText
              text={description}
              maxLength={30}
              title="Report description"
            />
          </Typography>
        )}
      </Box>
    );
  };
  // Author of the reported post. Cheating tickets also carry the accused user
  // directly (`userDetails` via reportedUserId), which survives the entry
  // being hard-deleted, so fall back to it there. Comment tickets carry the
  // commenter as `commentBy`.
  const getPostAuthor = (ticket) =>
    ticket?.postedBy || (isCheatingPage ? ticket?.userDetails : null) || null;
  const getTicketAuthor = (ticket) =>
    isCommentPage ? ticket?.commentBy || null : getPostAuthor(ticket);

  // Pagent summary for a cheating ticket: type + final vote split, with the
  // accused entry's votes first so the admin reads "accused – reporter".
  const getPagentCell = (ticket) => {
    const match = ticket?.matchDetails;
    if (!match) {
      return (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>—</Typography>
      );
    }
    const accusedIsPost1 =
      match.post1 && String(match.post1) === String(ticket._id);
    const accusedVotes = accusedIsPost1 ? match.post1Votes : match.post2Votes;
    const reporterVotes = accusedIsPost1 ? match.post2Votes : match.post1Votes;
    const accusedWon =
      match.winner && getPostAuthor(ticket)?._id
        ? String(match.winner) === String(getPostAuthor(ticket)._id)
        : null;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <Typography sx={{ fontSize: "inherit", fontWeight: 600 }}>
          {MATCH_TYPE_LABELS[match.matchType] || match.matchType || "Pagent"}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#666" }}>
          Votes {accusedVotes ?? 0} – {reporterVotes ?? 0}
          {match.forcefullyEnded ? " · forfeit" : ""}
        </Typography>
        {accusedWon != null && (
          <Chip
            size="small"
            label={accusedWon ? "Accused won" : "Accused lost"}
            color={accusedWon ? "warning" : "default"}
            sx={{ height: "18px", fontSize: "11px", fontWeight: 600, width: "fit-content" }}
          />
        )}
      </Box>
    );
  };

  const getBodyContent = (ticket, column) => {
    if (column.id === "actions") {
      const isUser = isAccountPage;
      const details = isUser
        ? ticket?.userDetails
        : isPostLike
        ? ticket?.postDetails
        : ticket?.commentDetails;
      const contentLabel = isPostPage
        ? "post"
        : isCheatingPage
        ? "entry"
        : filterType === "COMMENT"
        ? "comment"
        : "account";
      // For users "off" means the account is blocked or deleted; for content
      // it means disabled or deleted.
      const alreadyOff = isUser
        ? Boolean(!details || details.isAccountBlocked || details.isDeleted)
        : Boolean(details?.isDisabled || details?.isDeleted);
      const alreadyOffReason =
        !details || details?.isDeleted
          ? "deleted"
          : isUser
          ? "blocked"
          : "disabled";
      const isDisabling = disablingId === ticket._id;
      const authorState =
        isPostLike || isCommentPage
          ? getAuthorAccountState(getTicketAuthor(ticket))
          : null;
      const canDisableAccount = authorState === "active";
      const canBanAccount =
        isPostLike &&
        (authorState === "active" || authorState === "disabled");
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexWrap: "wrap",
          }}
        >
          <IconButton
            sx={{
              color: "#142C5C",
              "&:hover": { backgroundColor: "#142C5C", color: "#fff" },
            }}
            onClick={() => handleViewTicket(ticket._id)}
          >
            <Visibility />
          </IconButton>
          <Tooltip
            arrow
            title={
              alreadyOff
                ? `This ${contentLabel} is already ${alreadyOffReason}`
                : isUser
                ? "Disable this user's account (they can no longer log in)"
                : `Disable this ${contentLabel} for all users`
            }
          >
            {/* span keeps the tooltip working when the button is disabled */}
            <span>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={alreadyOff || isDisabling}
                onClick={() => handleDisableContent(ticket)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "78px",
                  whiteSpace: "nowrap",
                }}
              >
                {isDisabling
                  ? "Disabling…"
                  : alreadyOff
                  ? "Disabled"
                  : "Disable"}
              </Button>
            </span>
          </Tooltip>
          {isCommentPage && (
            <Tooltip
              arrow
              title={
                authorState === "banned"
                  ? "This account is already banned"
                  : authorState === "deleted"
                  ? "This account has been deleted"
                  : authorState === "disabled"
                  ? "This account is already disabled"
                  : "Disable the comment author's account. The comment stays visible."
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canDisableAccount || accountActionBusy}
                  onClick={() => openAccountDialog("DISABLE_ACCOUNT", ticket)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    color: "#c62828",
                    borderColor: "#c62828",
                    "&:hover": {
                      borderColor: "#b71c1c",
                      backgroundColor: "rgba(198, 40, 40, 0.06)",
                    },
                  }}
                >
                  {canDisableAccount
                    ? "Disable account"
                    : authorState === "deleted"
                    ? "Account deleted"
                    : "Account disabled"}
                </Button>
              </span>
            </Tooltip>
          )}
          {isPostLike && (
            <Tooltip
              arrow
              title={
                authorState === "banned"
                  ? "This account is already banned"
                  : authorState === "deleted"
                  ? "This account has been deleted"
                  : "Disable or ban the post author's account"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!canBanAccount || accountActionBusy}
                  onClick={(event) =>
                    setAccountMenu({
                      anchorEl: event.currentTarget,
                      ticket,
                    })
                  }
                  endIcon={<KeyboardArrowDown />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    color: "#c62828",
                    borderColor: "#c62828",
                    "&:hover": {
                      borderColor: "#b71c1c",
                      backgroundColor: "rgba(198, 40, 40, 0.06)",
                    },
                  }}
                >
                  Account
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    } else if (column.id === "createdAt") {
      // USER and CHEATING rows show the latest report date (the
      // aggregation's $first createdAt, reports sorted newest first).
      if (isAccountPage || isCheatingPage) {
        return formatDateString(ticket?.createdAt);
      }
      return formatDateString(
        ticket[filterType === "POST" ? "postDetails" : "commentDetails"]
          ?.createdAt
      );
    } else if (column.id === "profilePicture") {
      return (
        <Avatar
          src={ticket?.userDetails?.profilePicture || undefined}
          alt={ticket?.userDetails?.username || "Reported user"}
          sx={{ width: 50, height: 50, bgcolor: "#e0e0e0" }}
        >
          {!ticket?.userDetails?.profilePicture && (
            <Person sx={{ color: "#777" }} />
          )}
        </Avatar>
      );
    } else if (column.id === "email") {
      return ticket?.userDetails?.email || "—";
    } else if (column.id === "postImage" && ticket.postDetails) {
      const isPlayable = isVideoUrl(ticket.postDetails.media);
      return (
        <>
          <div style={{ position: "relative", width: "60px" }}>
            <img
              src={getImage(ticket)}
              alt="Post"
              width="50"
              height="50"
              style={{ objectFit: "cover", borderRadius: "4px" }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = typeOutline;
              }}
            />
            {isPlayable && (
              <Tooltip title="Play video" arrow>
                <Box
                  onClick={() => openVideoDialog(ticket)}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "50px",
                    height: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(0, 0, 0, 0.35)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.55)" },
                  }}
                >
                  <PlayArrow sx={{ color: "#fff" }} />
                </Box>
              </Tooltip>
            )}
            <Tooltip title={getPostBadge(ticket).label} arrow>
              <div style={postStyle}>{getPostBadge(ticket).letter}</div>
            </Tooltip>
          </div>
        </>
      );
    } else if (column.id === "postImage" && isCheatingPage) {
      // The accused entry can be deleted after the report was filed.
      return (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          Entry deleted
        </Typography>
      );
    } else if (column.id === "latestReport") {
      return getLatestReportCell(ticket);
    } else if (column.id === "pagent") {
      return getPagentCell(ticket);
    } else if (column.id === "reportedBy") {
      const reporter = ticket?.reporterDetails;
      return (
        <Box>
          <Typography sx={{ fontSize: "inherit", fontWeight: 600 }}>
            {limitChar(reporter?.username || "Deleted user")}
          </Typography>
          {reporter?.email && (
            <Typography sx={{ fontSize: "12px", color: "#777" }}>
              {reporter.email}
            </Typography>
          )}
        </Box>
      );
    } else if (column.id === "contentStatus") {
      if (isAccountPage) {
        return getUserStatusChip(ticket?.userDetails);
      }
      return getContentStatusChip(
        isPostLike ? ticket?.postDetails : ticket?.commentDetails
      );
    } else if (
      column.id === "caption" &&
      ticket.postDetails &&
      ticket.postDetails.caption
    ) {
      return (
        <ExpandableText
          text={ticket.postDetails.caption}
          maxLength={15}
          title="Post caption"
        />
      );
    } else if (column.id === "username") {
      if (isAccountPage) {
        return limitChar(ticket?.userDetails?.username || "Deleted user");
      }
      if (isPostLike || isCommentPage) {
        const author = getTicketAuthor(ticket);
        const authorState = getAuthorAccountState(author);
        return (
          <Box>
            <Typography sx={{ fontSize: "inherit" }}>
              {limitChar(author?.username || "Deleted user")}
            </Typography>
            {authorState !== "active" && (
              <Chip
                size="small"
                label={
                  authorState === "banned"
                    ? "Banned"
                    : authorState === "disabled"
                    ? "Disabled"
                    : "Deleted"
                }
                color={authorState === "deleted" ? "default" : "error"}
                sx={{
                  height: "18px",
                  fontSize: "11px",
                  fontWeight: 600,
                  marginTop: "4px",
                  ...(authorState === "deleted"
                    ? { backgroundColor: "#9e9e9e", color: "#fff" }
                    : {}),
                }}
              />
            )}
          </Box>
        );
      }
      return "—";
    } else if (column.id === "content") {
      // Show the whole comment, wrapped — admins need to read the full text
      // to judge a report without opening the detail page.
      return (
        <Typography
          sx={{
            fontSize: "inherit",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {ticket?.commentDetails?.content || "—"}
        </Typography>
      );
    } else if (column.id === "totalReportCount") {
      return ticket.totalReportCount;
    } else if (column.id === "aiAnalysis") {
      return renderAiAnalysisCell(ticket?.moderation);
    } else if (column.id === "status") {
      return (
        <CustomSwitch
          checked={!ticket?.isResolved}
          onChange={() => handleStatusToggle(ticket._id, ticket?.isResolved)}
        />
      );
    } else return ticket[column.id];
  };

  const getStyleValue = (comparator1, comparator2, style1, style2) => {
    if (comparator1 === comparator2) return style1;
    else return style2;
  };

  return (
    <PageLayout
      title="Reports"
      loading={loading}
      actions={
        <Box>
          <Button
            style={{ color: isPostPage ? "#ffffff" : "#142C5C" }}
            href="#"
            onClick={(event) => handleBreadcrumbClick(event, "POST")}
            sx={{
              border: "2px solid #142C5C",
              padding: "5px 20px",
              display: "inline-block",
              borderRadius: "6px",
              backgroundColor: isPostPage ? "#142C5C" : "#ffffff",
              fontSize: "14px",
              marginRight: "10px",
              "&:hover": {
                backgroundColor: "#142C5C",
                color: "#fff !important",
              },
            }}
          >
            Posts
          </Button>
          <Button
            style={{ color: isCommentPage ? "#ffffff" : "#142C5C" }}
            href="#"
            onClick={(event) => handleBreadcrumbClick(event, "COMMENT")}
            sx={{
              border: "2px solid #142C5C",
              padding: "5px 20px",
              display: "inline-block",
              borderRadius: "6px",
              backgroundColor: isCommentPage ? "#142C5C" : "#ffffff",
              fontSize: "14px",
              marginRight: "10px",
              "&:hover": {
                backgroundColor: "#142C5C",
                color: "#fff !important",
              },
            }}
          >
            Comments
          </Button>
          <Button
            style={{ color: isUserPage ? "#ffffff" : "#142C5C" }}
            href="#"
            onClick={(event) => handleBreadcrumbClick(event, "USER")}
            sx={{
              border: "2px solid #142C5C",
              padding: "5px 20px",
              display: "inline-block",
              borderRadius: "6px",
              backgroundColor: isUserPage ? "#142C5C" : "#ffffff",
              fontSize: "14px",
              marginRight: "10px",
              "&:hover": {
                backgroundColor: "#142C5C",
                color: "#fff !important",
              },
            }}
          >
            Users
          </Button>
          <Button
            style={{ color: isLivePage ? "#ffffff" : "#142C5C" }}
            href="#"
            onClick={(event) => handleBreadcrumbClick(event, "LIVE")}
            sx={{
              border: "2px solid #142C5C",
              padding: "5px 20px",
              display: "inline-block",
              borderRadius: "6px",
              backgroundColor: isLivePage ? "#142C5C" : "#ffffff",
              fontSize: "14px",
              marginRight: "10px",
              "&:hover": {
                backgroundColor: "#142C5C",
                color: "#fff !important",
              },
            }}
          >
            Live
          </Button>
          <Button
            style={{ color: isCheatingPage ? "#ffffff" : "#142C5C" }}
            href="#"
            onClick={(event) => handleBreadcrumbClick(event, "CHEATING")}
            sx={{
              border: "2px solid #142C5C",
              padding: "5px 20px",
              display: "inline-block",
              borderRadius: "6px",
              backgroundColor: isCheatingPage ? "#142C5C" : "#ffffff",
              fontSize: "14px",
              "&:hover": {
                backgroundColor: "#142C5C",
                color: "#fff !important",
              },
            }}
          >
            Cheating
          </Button>
        </Box>
      }
    >
      <CssBaseline />
      <Box>
        {!loading && (
          <>
            <Box
              sx={{
                padding: "15px 15px 0 15px",
                borderRadius: "6px",
                boxShadow:
                  "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
              }}
            >
              {isPostLike && (
                <Box
                  sx={{
                    marginBottom: "15px",
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  {Object.values(POST_TYPE_BADGES).map((badge) => (
                    <div key={badge.letter}>
                      <span style={{ color: "#142C5C", fontSize: "20px" }}>
                        {badge.letter}
                      </span>
                      -{badge.label}
                    </div>
                  ))}
                </Box>
              )}
              <TableContainer
                component={Paper}
                sx={{
                  maxHeight: "calc(100vh - 190px)",
                  minHeight: "calc(100vh - 190px)",
                  boxShadow: "none !important",
                }}
              >
                <Table
                  sx={{
                    border: "1px solid rgba(224, 224, 224, 1)",
                    minWidth: "100%",
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{ position: "sticky", top: "-1px", zIndex: 999 }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          sx={{
                            fontSize: {
                              xs: "14px",
                              sm: "15px",
                              md: "15px",
                              lg: "16px",
                              xl: "16px",
                            },
                            color: "#000",
                            fontWeight: "600",
                            padding: {
                              xs: "10px 5px",
                              sm: "15px 10px",
                              md: "15px 10px",
                              lg: "20px 15px",
                              xl: "20px 15px",
                            },
                            backgroundColor: "#e0e0e0",
                            "&:nth-child(2)": { width: "250px" },
                            verticalAlign: {
                              xs: "top",
                              sm: "top",
                              md: "top",
                            },
                          }}
                        >
                          {getHeaderContent(column)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tickets.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          sx={{
                            textAlign: "center",
                            height: "calc(100vh - 250px)",
                            fontSize: {
                              xs: "12px",
                              sm: "13px",
                              md: "14px",
                              lg: "15px",
                              xl: "15px",
                            },
                            color: "#333",
                            padding: {
                              xs: "10px 5px",
                              sm: "10px",
                              md: "10px",
                              lg: "10px 15px",
                              xl: "10px 15px",
                            },
                          }}
                        >
                          No Tickets found
                        </TableCell>
                      </TableRow>
                    ) : (
                      tickets.map((ticket) => (
                        <TableRow key={ticket._id}>
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              sx={{
                                fontSize: {
                                  xs: "12px",
                                  sm: "13px",
                                  md: "14px",
                                  lg: "15px",
                                  xl: "15px",
                                },
                                color: "#333",
                                padding: {
                                  xs: "10px 5px",
                                  sm: "10px",
                                  md: "10px 10px 10px 5px",
                                  lg: "10px 15px 10px 5px",
                                  xl: "10px 15px 10px 5px",
                                },
                                overflow: getStyleValue(
                                  column.id,
                                  "content",
                                  "",
                                  "visible"
                                ),
                                textOverflow: getStyleValue(
                                  column.id,
                                  "content",
                                  "",
                                  "clip"
                                ),
                                whiteSpace: getStyleValue(
                                  column.id,
                                  "content",
                                  "normal",
                                  "normal"
                                ),
                                maxWidth: getStyleValue(
                                  column.id,
                                  "content",
                                  "300px",
                                  "none"
                                ),
                                minWidth: getStyleValue(
                                  column.id,
                                  "content",
                                  "220px",
                                  "0"
                                ),
                                paddingLeft: getStyleValue(
                                  column.id,
                                  "status",
                                  "5px !important",
                                  "none"
                                ),
                              }}
                            >
                              {getBodyContent(ticket, column)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <CommonPagination
                count={totalRows}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                rowsPerPage={15}
              />
            </Box>
          </>
        )}
        <VideoPlayerDialog
          open={videoDialog.open}
          onClose={() => setVideoDialog((prev) => ({ ...prev, open: false }))}
          url={videoDialog.url}
          fallbackUrl={videoDialog.fallbackUrl}
          title={videoDialog.title}
          isDisabled={videoDialog.isDisabled}
        />
        <Menu
          anchorEl={accountMenu.anchorEl}
          open={Boolean(accountMenu.anchorEl)}
          onClose={closeAccountMenu}
        >
          <MenuItem
            disabled={
              getAuthorAccountState(getPostAuthor(accountMenu.ticket)) !==
                "active" || accountActionBusy
            }
            onClick={() =>
              openAccountDialog("DISABLE_ACCOUNT", accountMenu.ticket)
            }
          >
            Disable account
          </MenuItem>
          <MenuItem
            disabled={
              !["active", "disabled"].includes(
                getAuthorAccountState(getPostAuthor(accountMenu.ticket))
              ) || accountActionBusy
            }
            onClick={() =>
              openAccountDialog("BAN_ACCOUNT", accountMenu.ticket)
            }
            sx={{ color: "#c62828" }}
          >
            Ban account
          </MenuItem>
        </Menu>
        <AccountActionDialog
          open={accountDialog.open}
          action={accountDialog.action}
          username={getTicketAuthor(accountDialog.ticket)?.username}
          subject={isCommentPage ? "comment" : "post"}
          busy={accountActionBusy}
          onClose={() =>
            setAccountDialog({ open: false, action: null, ticket: null })
          }
          onConfirm={handleAccountAction}
        />
      </Box>
    </PageLayout>
  );
};

export default ManageTicket;
